import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function FuseBoxRepairsPage() {
  useEffect(() => {
    document.title = 'Fuse Box Repairs Watford | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Fuse box & consumer unit repairs in Watford. Fix tripping RCDs, faulty MCBs & electrical panel issues. NICEIC certified electricians. Call 020 3198 1818.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Fuse box & consumer unit repairs in Watford. Fix tripping RCDs, faulty MCBs & electrical panel issues. NICEIC certified electricians. Call 020 3198 1818.';
      document.head.appendChild(meta);
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beyond Electrical",
      "image": "https://beyondelectrical.co.uk/logo.png",
      "url": "https://beyondelectrical.co.uk/services/repairs/fuse-box-repairs",
      "telephone": "02031981818",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Watford",
        "addressLocality": "Watford",
        "addressRegion": "Hertfordshire",
        "postalCode": "WD17",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 51.6565,
        "longitude": -0.3957
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday"
        ],
        "opens": "00:00",
        "closes": "23:59"
      },
      "serviceType": "Fuse Box Repairs",
      "areaServed": {
        "@type": "City",
        "name": "Watford"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    { emoji: '🔧', title: 'MCB Replacement', description: 'Replacing faulty miniature circuit breakers that won\'t reset' },
    { emoji: '⚡', title: 'RCD Repairs', description: 'Fixing or replacing residual current devices that trip frequently' },
    { emoji: '🔌', title: 'Busbar Repairs', description: 'Repairing damaged busbars and connections inside the unit' },
    { emoji: '🛠️', title: 'Main Switch Replacement', description: 'Replacing worn or faulty main isolator switches' },
    { emoji: '💡', title: 'Circuit Labeling', description: 'Properly labeling all circuits for safety and convenience' },
    { emoji: '📋', title: 'Safety Inspections', description: 'Full inspection and testing to ensure safe operation' }
  ];

  const signs = [
    { emoji: '💥', title: 'Breakers Won\'t Reset', description: 'Circuit breakers trip and refuse to switch back on' },
    { emoji: '🔥', title: 'Burning Smell', description: 'Unusual odors coming from the consumer unit' },
    { emoji: '⚠️', title: 'Scorch Marks', description: 'Visible burn marks or discoloration on the unit' },
    { emoji: '🌡️', title: 'Hot to Touch', description: 'Consumer unit or breakers feel warm or hot' },
    { emoji: '⚡', title: 'Buzzing Sounds', description: 'Unusual humming or buzzing noises from the unit' },
    { emoji: '🔌', title: 'Loose Connections', description: 'Visible loose wires or connections inside the unit' }
  ];

  const benefits = [
    { emoji: '🛡️', title: 'Safety First', description: 'Faulty fuse boxes are dangerous - we ensure your unit is safe and compliant' },
    { emoji: '⚡', title: 'Expert Repairs', description: 'NICEIC certified electricians with 15+ years experience' },
    { emoji: '💰', title: 'Cost-Effective', description: 'Repairs often cheaper than full replacement when caught early' }
  ];

  const areaLinks = [
    { name: 'Abbots Langley', slug: 'abbots-langley' },
    { name: 'Borehamwood', slug: 'borehamwood' },
    { name: 'Bushey', slug: 'bushey' },
    { name: 'Chorleywood', slug: 'chorleywood' },
    { name: 'Croxley Green', slug: 'croxley-green' },
    { name: 'Edgware', slug: 'edgware' },
    { name: 'Elstree', slug: 'elstree' },
    { name: 'Harrow', slug: 'harrow' },
    { name: 'Mill Hill', slug: 'mill-hill' },
    { name: 'Moor Park & Northwood', slug: 'moor-park-northwood' },
    { name: 'Pinner', slug: 'pinner' },
    { name: 'Potters Bar', slug: 'potters-bar' },
    { name: 'Radlett', slug: 'radlett' },
    { name: 'Rickmansworth', slug: 'rickmansworth' },
    { name: 'Ruislip', slug: 'ruislip' },
    { name: 'South Oxhey', slug: 'south-oxhey' },
    { name: 'Stanmore', slug: 'stanmore' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0A1628] via-[#0F1F3A] to-[#0A1628] text-white pt-32 pb-20">
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/03edb718fdbd14834e63c7ee68211e22.jpg')] bg-cover bg-center opacity-10"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Fuse Box Repairs in Watford
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              Expert consumer unit and fuse box repairs. Fast, safe, and certified repairs to keep your electrical system running smoothly.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-10">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <i className="ri-shield-check-line text-[#D4AF37]"></i>
                <span className="text-sm whitespace-nowrap">NICEIC Approved</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <i className="ri-time-line text-[#D4AF37]"></i>
                <span className="text-sm whitespace-nowrap">Same-Day Service</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <i className="ri-map-pin-line text-[#D4AF37]"></i>
                <span className="text-sm whitespace-nowrap">Serving Watford</span>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:02031981818" className="bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1628] px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-phone-line"></i>
                Call 020 3198 1818
              </a>
              <a href="mailto:info@beyondelectrical.co.uk" className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-mail-line"></i>
                Get a Quote
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                Our Fuse Box Repair Services
              </h2>
              <p className="text-xl text-gray-600">
                Comprehensive repairs for all consumer unit issues
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                  <div className="w-16 h-16 flex items-center justify-center text-4xl mb-4">
                    {service.emoji}
                  </div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Warning Signs */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                Warning Signs Your Fuse Box Needs Repair
              </h2>
              <p className="text-xl text-gray-600">
                Don't ignore these danger signals - contact us immediately
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {signs.map((sign, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all duration-300">
                  <div className="w-16 h-16 flex items-center justify-center text-4xl mb-4">
                    {sign.emoji}
                  </div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-3">
                    {sign.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {sign.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                Why Choose Our Fuse Box Repair Service
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg text-center">
                  <div className="w-20 h-20 flex items-center justify-center text-5xl mx-auto mb-6">
                    {benefit.emoji}
                  </div>
                  <h3 className="text-2xl font-bold text-[#0A1628] mb-4">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Beyond Electrical */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#0F1F3A] to-[#0A1628] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                Why Choose Beyond Electrical
              </h2>
              <p className="text-xl text-gray-300">
                Trusted fuse box repair experts in Watford
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-[#D4AF37] rounded-full mx-auto mb-4">
                  <i className="ri-time-line text-4xl text-[#0A1628]"></i>
                </div>
                <h3 className="text-2xl font-bold mb-3">Fast Response</h3>
                <p className="text-gray-300">Same-day service for urgent fuse box repairs</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-[#D4AF37] rounded-full mx-auto mb-4">
                  <i className="ri-shield-check-line text-4xl text-[#0A1628]"></i>
                </div>
                <h3 className="text-2xl font-bold mb-3">NICEIC Certified</h3>
                <p className="text-gray-300">All work certified and compliant with regulations</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-[#D4AF37] rounded-full mx-auto mb-4">
                  <i className="ri-award-line text-4xl text-[#0A1628]"></i>
                </div>
                <h3 className="text-2xl font-bold mb-3">15+ Years Experience</h3>
                <p className="text-gray-300">Trusted by hundreds of Watford homeowners</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">400+</div>
                <div className="text-gray-300">Fuse Boxes Repaired</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">100%</div>
                <div className="text-gray-300">Satisfaction Rate</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">24/7</div>
                <div className="text-gray-300">Emergency Service</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">15+</div>
                <div className="text-gray-300">Years Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
              Fuse Box Repair Areas We Cover
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              Click your area for local fuse box repair services with area-specific information
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {areaLinks.map((area, index) => (
                <Link
                  key={index}
                  to={`/services/repairs/fuse-box-repairs/${area.slug}`}
                  className="bg-gray-50 hover:bg-[#D4AF37] text-[#0A1628] hover:text-white px-4 py-3 rounded-lg font-medium text-sm transition-all duration-300 cursor-pointer text-center"
                >
                  {area.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#0F1F3A] to-[#0A1628] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Need Fuse Box Repairs in Watford?
            </h2>
            <p className="text-xl text-gray-300 mb-10">
              Contact us today for expert consumer unit repairs. Fast, safe, and certified service.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:02031981818" className="bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1628] px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-phone-line"></i>
                Call 020 3198 1818
              </a>
              <a href="mailto:info@beyondelectrical.co.uk" className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-mail-line"></i>
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
